import { useEffect, useRef, useState } from 'react';
import { Dimensions } from 'react-native';
import { getVersion } from 'react-native-device-info';
import {
  useCropPreferenceStore,
  useProfileStore,
  KissanEventType,
  KVPair,
} from '@ninjacart/nc-nk-store';
import {
  INinjaEvent,
  addEventParam,
  useEventStore,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';

const DEFAULT_WEB_AD_SIZES: googletag.GeneralSize = [
  [240, 133],
  [250, 250],
  [250, 360],
  [292, 30],
  [300, 50],
  [300, 75],
  [300, 100],
  [300, 250],
  [320, 50],
  [320, 100],
  [320, 480],
  [336, 280],
  [350, 100],
  [350, 200],
  [350, 250],
  [400, 300],
  // [300, 600], // Half Page / Skyscraper
  // [160, 600], // Wide Skyscraper
  // [120, 600], // Skyscraper
  // [250, 250], // Square
  // [336, 280], // Large Rectangle
];

// Waits until window.googletag.cmd is defined and usable
const waitForGoogletagReady = (timeout = 5000): Promise<typeof window.googletag> => {
  return new Promise((resolve, reject) => {
    const interval = 100;
    let waited = 0;
    const check = () => {
      if (window?.googletag && window.googletag?.cmd) return resolve(window.googletag);
      if ((waited += interval) >= timeout) return reject('GPT script not ready in time');
      setTimeout(check, interval);
    };
    check();
  });
};

// Injects GPT script if not already present
const injectGPTScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('#gpt-script')) return resolve();
    const script = document.createElement('script');
    script.id = 'gpt-script';
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

type FilterAdItemsByScreenWidth = (
  data: googletag.MultiSize,
  screenWidth: number
) => googletag.MultiSize;

export const BannerAds = ({
  deviceWidth,
  itemKey,
  unitId,
  bannerAdKeywords,
  adsPreloadData,
  adSizeDefs = [],
  onAdSizeChange,
  onAdClosed,
  webAdSizeDefs = DEFAULT_WEB_AD_SIZES,
  adgebraShow = true,
  screenName,
}: {
  deviceWidth: number;
  itemKey: string;
  unitId: string;
  bannerAdKeywords?: string[];
  adsPreloadData?: KVPair;
  adSizeDefs?: string[];
  onAdSizeChange?: (height: number, width: number) => void;
  onAdClosed?: () => void;
  webAdSizeDefs?: googletag.GeneralSize;
  adgebraShow?: boolean;
  screenName: string;
}) => {
  const filterByScreenWidth: FilterAdItemsByScreenWidth = (data, screenWidth) => {
    return data.filter(([width]) => (width as number) <= screenWidth);
  };
  // const adSlotsByDeviceWidthSupported = filterByScreenWidth(webAdSizesDefaults, screenWidth);
  const adSlotRef = useRef<HTMLDivElement>(null);
  const [isAdLoaded, setIsAdLoaded] = useState(false);
  const [isDev, setIsDev] = useState<boolean>();

  const appVersion = getVersion();
  const userProfile = useProfileStore((state) => state.userProfile);
  const animalPreference = useCropPreferenceStore((state) => state.animalPreference);
  const cropPreference = useCropPreferenceStore((state) => state.cropPreference);
  const address = userProfile?.user?.contactDetails?.address?.[0];
  const eventUserDetails = useEventStore((state) => state?.eventUserDetails);
  const processEvents = useQueuedEventStore.getState()?.processEvents;

  const adSizes = webAdSizeDefs?.length > 0 ? webAdSizeDefs : [];

  useEffect(() => {
    setIsDev(
      window.location.origin.includes('localhost') || window.location.origin.includes('trafyn.info')
    );
  }, []);

  useEffect(() => {
    if (isDev === undefined) return;

    let slot: googletag.Slot | undefined = undefined;
    let slotRequestedHandler: any;
    let slotRenderEndedHandler: any;

    const loadAds = async () => {
      try {
        await injectGPTScript();
        await waitForGoogletagReady();

        const adUnitId = isDev ? '/6355419/Travel/Europe/France/Paris' : unitId;

        window.googletag.cmd.push(() => {
          slot = window.googletag
            .defineSlot(adUnitId, adSizes, itemKey)
            ?.addService(window.googletag.pubads());

          if (!slot) return;

          // Targeting
          const targetingMap = {
            appVersion,
            userId: userProfile?.user?.id,
            systemUserId: userProfile?.realm?.systemUserId,
            primaryPhoneNumber: userProfile?.user?.primaryPhoneNumber,
            ...(address && {
              villageName: address.village,
              districtName: address.district,
              stateName: address.state,
              pincode: address.pincode,
            }),
            latitude: eventUserDetails?.latitude ?? '',
            longitude: eventUserDetails?.longitude ?? '',
            animalPreferenceNames: animalPreference.map((a) => a?.name).filter(Boolean),
            cropPreferenceNames: cropPreference.map((c) => c?.name).filter(Boolean),
          };

          Object.entries(targetingMap).forEach(([k, v]) => {
            if (v) slot!.setTargeting(k, v);
          });

          // Event listeners
          slotRequestedHandler = (event: googletag.events.SlotRequestedEvent) => {
            if (event.slot === slot) {
              const eventPayload: INinjaEvent = {
                name: KissanEventType.AD_SLOT_LOAD_REQUEST,
                screenDetails: { screen: 'bannerAd page' },
                eventParams: [
                  addEventParam('adUnitId', adUnitId),
                  addEventParam('itemId', itemKey),
                ],
              };
              processEvents?.([eventPayload]);
            }
          };

          slotRenderEndedHandler = (event: googletag.events.SlotRenderEndedEvent) => {
            if (event.slot === slot && event.size) {
              const [w, h] = event.size;
              adSlotRef.current!.style.width = `${w}px`;
              adSlotRef.current!.style.height = `${h}px`;
              setIsAdLoaded(true);
              onAdSizeChange?.(Number(h), Number(w));
            }
          };

          window.googletag.pubads().addEventListener('slotRequested', slotRequestedHandler);
          window.googletag.pubads().addEventListener('slotRenderEnded', slotRenderEndedHandler);

          window.googletag.enableServices();
          window.googletag.display(itemKey);
        });
      } catch (err) {
        console.error('Failed to load GPT ads:', err);
      }
    };

    loadAds();

    return () => {
      try {
        if (window.googletag?.pubads) {
          window.googletag.pubads().removeEventListener('slotRequested', slotRequestedHandler);
          window.googletag.pubads().removeEventListener('slotRenderEnded', slotRenderEndedHandler);

          if (slot) {
            window.googletag.destroySlots([slot]);
          }
        }
      } catch (cleanupErr) {
        console.warn('Error during ad slot cleanup', cleanupErr);
      }
    };
  }, [itemKey, isDev]);

  return (
    <div
      id={itemKey}
      ref={adSlotRef}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        margin: '0 auto',
        visibility: isAdLoaded ? 'visible' : 'hidden',
      }}
    />
  );
};
